import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

const ForgotPasswordSuccess = ({ email, navigate, onResend }) => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-700">
      <div className="w-16 h-16 bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-center text-white mb-4">Check Your Email</h2>
      <p className="text-gray-300 text-center mb-2">
        If an admin account exists with <span className="font-semibold text-white">{email}</span>, you will receive a password reset link shortly.
      </p>
      <p className="text-sm text-gray-400 text-center mb-6">The link will expire in 15 minutes for security.</p>
      <button onClick={() => navigate('/admin/login')} className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition">Back to Admin Login</button>
      <button onClick={onResend} className="w-full mt-3 text-green-400 hover:text-green-300 font-medium text-sm">Didn't receive it? Send again</button>
    </div>
  </div>
);

const AdminForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/request-password-reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });
      if (res.ok) setSubmitted(true);
      else { const data = await res.json(); setError(data.detail || 'Failed to send reset email'); }
    } catch (err) { logger.error(err); setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  if (submitted) return <ForgotPasswordSuccess email={email} navigate={navigate} onResend={() => setSubmitted(false)} />;

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-700">
        <button onClick={() => navigate('/admin/login')} className="flex items-center text-gray-400 hover:text-white mb-6 transition">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Admin Login
        </button>
        <div className="w-16 h-16 bg-linear-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-center text-white mb-2">Forgot Password?</h1>
        <p className="text-gray-400 text-center mb-8">Enter your admin email and we'll send you a reset link.</p>
        {error && <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg"><p className="text-red-400 text-sm">{error}</p></div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
            <input type="email" data-testid="admin-forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" required
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent transition" />
          </div>
          <button type="submit" disabled={loading} data-testid="admin-forgot-submit"
            className="w-full bg-linear-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50">
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        <p className="text-xs text-gray-500 text-center mt-6">
          Remember your password?{' '}
          <button onClick={() => navigate('/admin/login')} className="text-green-400 hover:text-green-300 font-medium">Sign in</button>
        </p>
      </div>
    </div>
  );
};

export default AdminForgotPasswordPage;
